import { useState, useCallback, useRef } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

// DocuSign routes now live on node-server (merged in alongside
// /google-calendar, /telnyx, /api/transfers), deployed at
// https://ai-realtor-2ktt.onrender.com. `FRONTEND_URL` was never a valid
// CRA env var here (it's unprefixed, and it's the wrong variable anyway —
// that name is used server-side for DocuSign's own return-to-app redirect).
const DOCUSIGN_SERVER_URL =
  process.env.REACT_APP_NODE_SERVER_URL ||
  (process.env.NODE_ENV === "development"
    ? "http://localhost:4000"
    : "https://ai-realtor-2ktt.onrender.com");

export const AGREEMENT_TYPES = {
  BLANKET_SERVICE: "blanketService",
  EQUIPMENT_LEASE: "equipmentLease",
};

const ONE_TIME_AGREEMENTS = [AGREEMENT_TYPES.BLANKET_SERVICE];

export function useDocuSignAgreement(agreementType) {
  const [checking, setChecking]  = useState(false);
  const [signing, setSigning]    = useState(false);
  const [error, setError]        = useState(null);
  const pollRef                  = useRef(null);

  const isOneTime = ONE_TIME_AGREEMENTS.includes(agreementType);

  const checkAgreement = useCallback(
    async (uid) => {
      if (!isOneTime) return { signed: false, signedAt: null, signedBy: null };
      if (!uid) return { signed: false, signedAt: null, signedBy: null };

      setChecking(true);
      setError(null);
      try {
        const ref = doc(db, "agreements", uid, "types", "findMeATenant");
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          return {
            signed: !!data.signed,
            signedAt: data.signedAt ? new Date(data.signedAt) : null,
            signedBy: data.signerEmail ?? null,
          };
        }
        return { signed: false, signedAt: null, signedBy: null };
      } catch (err) {
        console.error(`[useDocuSignAgreement:${agreementType}] checkAgreement error:`, err);
        setError(err.message || "Failed to check agreement status.");
        return { signed: false, signedAt: null, signedBy: null };
      } finally {
        setChecking(false);
      }
    },
    [isOneTime, agreementType]
  );
  const getSigningUrl = useCallback(
    async (uid, email, fullName, prefillTabs = null, orderId = null) => {
      if (!uid || !email || !fullName) {
        throw new Error("uid, email, and full name are required to sign.");
      }
      if (agreementType === AGREEMENT_TYPES.EQUIPMENT_LEASE && !orderId) {
        throw new Error("orderId is required for Equipment Lease signing.");
      }

      setSigning(true);
      setError(null);
      try {
        const res = await fetch(`${DOCUSIGN_SERVER_URL}/api/docusign/create-envelope`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            uid,
            email,
            fullName,
            agreementType,
            prefillTabs,
            orderId,
          }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.error || "Failed to start DocuSign signing.");
        }
        return await res.json();
      } catch (err) {
        console.error(`[useDocuSignAgreement:${agreementType}] getSigningUrl error:`, err);
        setError(err.message || "Failed to start signing. Please try again.");
        throw err;
      } finally {
        setSigning(false);
      }
    },
    [agreementType]
  );

  /**
   * Polls envelope status every 3 seconds.
   * Calls onComplete() when DocuSign reports "completed".
   * Returns a cleanup function.
   */
  const pollEnvelopeStatus = useCallback(
    (uid, envelopeId, onComplete, orderId = null) => {
      if (pollRef.current) clearInterval(pollRef.current);

      pollRef.current = setInterval(async () => {
        try {
          const params = new URLSearchParams({
            uid,
            agreementType,
            ...(orderId ? { orderId } : {}),
          });
          const res = await fetch(
            `${DOCUSIGN_SERVER_URL}/api/docusign/envelope-status/${envelopeId}?${params}`
          );
          if (!res.ok) return;
          const { status } = await res.json();
          if (status === "completed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
            onComplete?.();
          }
        } catch (err) {
          console.warn(
            `[useDocuSignAgreement:${agreementType}] poll error (will retry):`,
            err.message
          );
        }
      }, 3000);

      return () => {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      };
    },
    [agreementType]
  );

  return {
    checkAgreement,
    getSigningUrl,
    pollEnvelopeStatus,
    checking,
    signing,
    error,
  };
}